import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from 'shared/components/ui/use-toast';

import { ChatScrollContext } from './hooks/useChatScrollContext';
import { useChatScrollToBottom } from './hooks';

import { useStreetBeatChat, useConversationFlow, useChatErrors, useTasks } from '@dev-streetbeat/chat';

import usePrevious from 'shared/hooks/usePrevious';

import { MessagesList } from './MessagesList';
import { ChatWelcome } from './components/ChatWelcome';
import { ChatInput, MessageData } from './components/ChatInput';
import { ChatGradient } from './components/ChatGradient';
import { ScrollArea, ScrollAreaRef } from 'shared/components/ui/scroll-area';
import { AssistantTaskUpdate } from 'types/AssistantTypes';

import styles from './chatPanel.module.scss';
import { useTranslation } from 'react-i18next';

export const ChatPanel: React.FC = () => {
  const {
    messages,
    pendingAction,
    createThreadAndStart,
    reconnectToThread,
    continueThread,
    cancel,
    threadId,
    streamingMessage,
    isConnectingToThread,
    reset,
  } = useStreetBeatChat();
  const { isLoading: runInProgress } = useConversationFlow();
  const { timeoutError, hasTimeoutError, clearTimeoutError, hasError, clearError } = useChatErrors();
  const { tasks, getLatestTask } = useTasks();
  const navigate = useNavigate();
  const params = useParams();
  const { toast } = useToast();
  const { t } = useTranslation();

  const [showArrow, setShowArrow] = useState(false);
  const [isCreatingThread, setIsCreatingThread] = useState(false);

  const currentThreadId = useMemo(() => params.threadId, [params.threadId]);
  const previousThreadId = usePrevious(currentThreadId);

  const previousPendingAction = usePrevious(pendingAction);
  const previousMessages = usePrevious(messages);

  // Get the latest active task for display
  const latestActiveTask = getLatestTask();

  const isThreadInitialized = !!currentThreadId; // Connection is open only when we have a threadId

  const showWelcomeContainer = (!isThreadInitialized && !isCreatingThread) || isConnectingToThread;
  const showMessagesContainer = !isConnectingToThread && (isThreadInitialized || isCreatingThread);
  const showInput = !isConnectingToThread || !isThreadInitialized;

  const scrollAreaRef = useRef<ScrollAreaRef>(null);

  const showError = useCallback(
    (errorMessage: string) => {
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      if (hasError) {
        clearError();
      }
    },
    [hasError, clearError],
  );

  useEffect(() => {
    if (hasTimeoutError) {
      showError(timeoutError);
      clearTimeoutError();
    }
  }, [timeoutError]);

  const handleSendMessage = useCallback(
    async (data: MessageData): Promise<boolean> => {
      if (!data.text?.trim()) {
        return false; // Don't send empty messages
      }

      let errorOccurred = false;

      try {
        if (!currentThreadId) {
          setIsCreatingThread(true);
          await createThreadAndStart(data.text);
        } else {
          await continueThread(currentThreadId, data.text);
        }
      } catch (error) {
        console.error('Failed to send message:', error);
        showError(error.message);
        errorOccurred = true;
      } finally {
        setIsCreatingThread(false);
      }

      return !errorOccurred;
    },
    [currentThreadId],
  );

  useEffect(() => {
    const connectToThread = async () => {
      try {
        await reconnectToThread(currentThreadId);
      } catch (error) {
        console.error('Failed to reconnect to thread:', error);
        const isSignalAbortError = error.message.includes('signal is aborted without reason');
        if (!isSignalAbortError) {
          showError(error.message);
        }
      }
    };

    // "!threadId" is important to avoid resetting the component state when we are navigating after thread
    if (currentThreadId !== previousThreadId && previousThreadId) {
      reset();
      setShowArrow(false);
      setIsCreatingThread(false);
    }

    if (currentThreadId) {
      connectToThread();
    }
  }, [currentThreadId]);

  useEffect(() => {
    if (threadId && !currentThreadId) {
      navigate(`/assistant/${threadId}`);
    }
  }, [threadId]);

  const { scrollToBottom, messagesEndRef } = useChatScrollToBottom();

  const showScrollToBottomButton = (_event: React.UIEvent<HTMLDivElement>, isAtBottom: boolean) => {
    setShowArrow(!isAtBottom);
  };

  // Handle message updates and smart scrolling
  useEffect(() => {
    if (showArrow) return;
    const isPendingActionChanged = previousPendingAction !== pendingAction && !!pendingAction;
    const isMessagesChanged = previousMessages !== messages && messages.length > 0;

    const smoothScroll = isPendingActionChanged || isMessagesChanged;

    scrollToBottom(smoothScroll);
  }, [tasks, messages, pendingAction, streamingMessage, showArrow, scrollToBottom]);

  const taskUpdate = useMemo(() => {
    if (!runInProgress) return undefined;
    if (pendingAction) {
      return {
        update: t('assistant.status.waitingForYourInput'),
        status: 'pending',
      } as AssistantTaskUpdate;
    }
    if (latestActiveTask) {
      return {
        update:
          latestActiveTask.message ||
          latestActiveTask.title ||
          latestActiveTask.description ||
          t('assistant.status.processing'),
        status: latestActiveTask.is_completed ? 'completed' : 'pending',
        progress_percentage: latestActiveTask.progress ? Math.round(latestActiveTask.progress * 100) : undefined,
        detailed_message: `${t('assistant.tasks.step')} ${latestActiveTask.current_step}/${latestActiveTask.total_steps}`,
      } as AssistantTaskUpdate;
    }

    // Default if run is in progress
    return { update: t('assistant.status.processing'), status: 'pending' } as AssistantTaskUpdate;
  }, [latestActiveTask, runInProgress, t, pendingAction]);

  const scrollContextValue = useMemo(
    () => ({
      scrollToBottom,
      messagesEndRef,
    }),
    [scrollToBottom],
  );

  return (
    <ChatScrollContext.Provider value={scrollContextValue}>
      <div className={styles.container}>
        {/* Welcome screen when no messages or no threadId */}
        {showWelcomeContainer && (
          <ChatWelcome
            sendUserMessage={handleSendMessage}
            isDisabled={runInProgress}
            isConnectionOpen={!isConnectingToThread}
          />
        )}

        {/* Messages list */}
        {showMessagesContainer && (
          <>
            <ChatGradient
              direction="to bottom"
              classNameInner="-mb-10 h-12"
              classNameOuter="pointer-events-none z-10 !-mb-2"
            />

            <ScrollArea ref={scrollAreaRef} onScroll={showScrollToBottomButton} className={styles.scrollArea}>
              <div className={styles.messagesList}>
                <MessagesList />
              </div>

              <div className={styles.messagesSpacer} />
              <div ref={messagesEndRef} />
            </ScrollArea>
          </>
        )}

        {/* Chat input for when we have messages */}
        {showInput && (
          <div className={styles.chatInputContainer}>
            <ChatInput
              onSend={handleSendMessage}
              onCancel={cancel}
              taskUpdate={taskUpdate}
              disabled={runInProgress}
              showArrow={showArrow}
              arrowAction={() => scrollToBottom(true)}
            />
          </div>
        )}

        {/* Bottom spacer for consistent padding */}
        <div className={styles.bottomSpacer} />
      </div>
    </ChatScrollContext.Provider>
  );
};
